import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Calendar, Compass, Users, Bell, MessageSquare, Search, LogOut, Dumbbell, User } from 'lucide-react';
import { notificationsAPI, connectionsAPI, authAPI } from '../api/client';
import '../styles/layout.css';

const NAV_ITEMS = [
  { label: 'Feed', icon: LayoutDashboard, path: '/feed' },
  { label: 'Activities', icon: Dumbbell, path: '/activities' },
  { label: 'Calendar', icon: Calendar, path: '/calendar' },
  { label: 'Discover', icon: Compass, path: '/discover' },
  { label: 'Groups & Friends', icon: Users, path: '/groups' },
  { label: 'Messages', icon: MessageSquare, path: '/messages' },
];

const MOBILE_NAV_ITEMS = [
  { label: 'Feed', icon: LayoutDashboard, path: '/feed' },
  { label: 'Activities', icon: Dumbbell, path: '/activities' },
  { label: 'Groups', icon: Users, path: '/groups' },
  { label: 'Messages', icon: MessageSquare, path: '/messages' },
  { label: 'Notifs', icon: Bell, path: '/notifications' },
  { label: 'Profile', icon: User, path: '/profile' },
];

function handleLogout(navigate) {
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('displayName');
  navigate('/login');
}

export default function Layout({ children, title = 'Covo', hasNotif = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const displayName = localStorage.getItem('displayName') || '?';
  const initial = displayName[0].toUpperCase();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const currentUserId = localStorage.getItem('userId');

  const isActive = (path) => location.pathname === path || (path === '/feed' && location.pathname === '/');

  useEffect(() => {
    const fetchUnread = () => {
      notificationsAPI.getAll()
        .then(r => setUnreadCount((Array.isArray(r.data) ? r.data : []).filter(n => !n.read_at).length))
        .catch(() => {});
      connectionsAPI.getAll()
        .then(r => setPendingCount((Array.isArray(r.data) ? r.data : []).filter(c => c.status === 'pending' && c.connected_user_id === currentUserId).length))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    authAPI.getMe().then(r => {
      const me = r.data;
      const isEmailUser = me?.phone_email?.includes('@');
      if (isEmailUser && me.email_verified) {
        localStorage.setItem('emailVerified', '1');
        setEmailUnverified(false);
      } else if (isEmailUser && !me.email_verified && localStorage.getItem('emailVerified') !== '1') {
        setEmailUnverified(true);
      }
    }).catch(() => {});
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="layout-sidebar">
        <div className="sidebar-brand" onClick={() => navigate('/feed')} style={{ cursor: 'pointer' }}>
          <span className="sidebar-logo">COVO</span>
          <span className="sidebar-tagline">come with</span>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map(({ label, path }) => (
            <button
              key={path}
              className={`sidebar-nav-item${isActive(path) ? ' active' : ''}`}
              onClick={() => navigate(path)}
            >
              {label}
            </button>
          ))}
        </nav>

        <button className="sidebar-new-plan" onClick={() => navigate('/create-activity')}>
          + New Activity
        </button>

        <div className="sidebar-bottom">
          <div className="sidebar-user" onClick={() => navigate('/profile')}>
            <div className="sidebar-avatar-wrap">
              <div className="sidebar-avatar">{initial}</div>
              {pendingCount > 0 && <span className="sidebar-pending-badge">{pendingCount}</span>}
            </div>
            <div className="sidebar-user-name">{displayName}</div>
          </div>
          <button className="sidebar-logout-btn" onClick={() => handleLogout(navigate)} title="Log out">
            <LogOut size={16} />
          </button>
        </div>
        <button className="sidebar-privacy-link" onClick={() => navigate('/privacy')}>
          Privacy policy
        </button>
      </aside>

      {/* Topbar */}
      <header className="layout-topbar">
        <span className="layout-title">{title}</span>
        <div className="layout-topbar-right">
          <button className="layout-icon-btn" onClick={() => navigate('/find-friends')}>
            <Search size={19} />
          </button>
          <button className="layout-icon-btn" onClick={() => { navigate('/notifications'); setUnreadCount(0); }}>
            <Bell size={19} />
            {(hasNotif || unreadCount > 0 || emailUnverified) && (
              <span className="layout-notif-dot" style={emailUnverified && unreadCount === 0 ? { background: '#D93868' } : {}}>
                {unreadCount > 0 && unreadCount < 10 ? unreadCount : unreadCount >= 10 ? '9+' : ''}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Page content */}
      <main className="layout-main">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="layout-bottom-nav">
        {MOBILE_NAV_ITEMS.map(({ label, icon: Icon, path }) => (
          <button
            key={path}
            className={`bottom-nav-item${isActive(path) ? ' active' : ''}`}
            onClick={() => navigate(path)}
          >
            <Icon size={22} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
